import React from "react";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericTooltip from "@ui-components/Tooltip/GenericTooltip";
import {
  PRIORITY_OPTIONS,
  isGeneralInfoValidCR,
  isAdditionalInfoValid as isAdditionalInfoValidShared,
} from "../../utils/tickets";
import type { UseTopicsLoaderReturn } from "../../hooks/useTopicsLoader";
import type { UseChangeRequestFormReturn } from "../../hooks/useChangeRequestForm";

interface RequestInfoTabProps {
  isKontrolEdge: boolean;
  topicsLoader: UseTopicsLoaderReturn;
  form: UseChangeRequestFormReturn;
  cloud: string;
  region: string;
  onBack: () => void;
  onNext: () => void;
}

export const RequestInfoTab: React.FC<RequestInfoTabProps> = ({
  isKontrolEdge,
  topicsLoader,
  form,
  cloud,
  region,
  onBack,
  onNext,
}) => {
  return (
    <div className="space-y-4 text-sm text-gray-700">
      <div className="flex gap-4">
        <div className="w-1/2">
          <label className="block mb-1 font-medium text-semantic-text-secondary">
            Priority <span className="text-red-500">*</span>
          </label>
          <GenericDropdown
            items={[...PRIORITY_OPTIONS]}
            value={form.formState.priority}
            onSelect={form.setPriority}
            defaultLabel="Select Priority"
            minWidthClass=""
            maxWidthClass=""
            widthClass="w-full"
            fixedPosition={true}
          />
        </div>
      </div>

      {!isKontrolEdge && (
        <>
          <div className="flex gap-4">
            <div className="w-1/2">
              <label className="block mb-1 font-medium text-semantic-text-secondary">
                Cloud <span className="text-red-500">*</span>
              </label>
              <GenericInput value={cloud} disabled />
            </div>
            <div className="w-1/2">
              <label className="block mb-1 font-medium text-semantic-text-secondary">
                Region <span className="text-red-500">*</span>
              </label>
              <GenericInput value={region} disabled />
            </div>
          </div>
        </>
      )}

      <div>
        <label className="mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
          Title <span className="text-red-500">*</span>
          <GenericTooltip
            text="At least 5 characters (ignoring spaces & punctuation)."
            position="top"
          />
        </label>
        <GenericInput
          placeholder="Write a brief description"
          value={form.formState.title}
          onChange={form.setTitle}
        />
      </div>

      <div>
        <label className="mb-1 font-medium flex items-center gap-1 text-semantic-text-secondary">
          Description <span className="text-red-500">*</span>
          <GenericTooltip
            text="At least 10 characters (ignoring spaces & punctuation)."
            position="top"
          />
        </label>
        <textarea
          className="w-full h-24 px-3 py-2 border border-semantic-border-secondary bg-semantic-bg-primary rounded-sm text-sm text-semantic-text-secondary"
          placeholder="Description of the problem (required)"
          value={form.formState.description}
          onChange={(e) => form.setDescription(e.target.value)}
        />
      </div>

      <div className="flex justify-end pt-2 gap-2">
        <GenericButton
          label="Back"
          variant="secondary"
          onClick={onBack}
        />
        <GenericButton
          label="Next"
          variant="brand"
          onClick={onNext}
          disabled={
            !isGeneralInfoValidCR(
              isKontrolEdge,
              topicsLoader.selectedTopicLabel,
              topicsLoader.selectedTwoLevelLabel
            ) ||
            !isAdditionalInfoValidShared(isKontrolEdge, {
              cloud,
              region,
              title: form.formState.title,
              description: form.formState.description,
              priority: form.formState.priority,
            })
          }
        />
      </div>
    </div>
  );
};

